import React from 'react';
import styled, { css } from 'styled-components';

// Create a button component that can forward refs
const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  border-radius: var(--border-radius-md);
  transition: var(--transition);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.875rem;
  cursor: pointer;
  
  ${({ variant }) => {
    switch(variant) {
      case 'primary':
        return css`
          background-color: var(--primary);
          color: var(--white);
          
          &:hover {
            background-color: var(--dark);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
      case 'secondary':
        return css`
          background-color: var(--secondary);
          color: var(--white);
          
          &:hover {
            background-color: var(--accent);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: var(--primary);
          border: 2px solid var(--primary);
          
          &:hover {
            background-color: var(--primary);
            color: var(--white);
            transform: translateY(-2px);
          }
        `;
      case 'text':
        return css`
          background-color: transparent;
          color: var(--primary);
          padding: 0.5rem;
          
          &:hover {
            color: var(--accent);
            background-color: rgba(0, 0, 0, 0.05);
          }
        `;
      default:
        return css`
          background-color: var(--primary);
          color: var(--white);
          
          &:hover {
            background-color: var(--dark);
            transform: translateY(-2px);
            box-shadow: var(--shadow-md);
          }
        `;
    }
  }}
  
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  ${({ size }) => {
    switch(size) {
      case 'small':
        return css`
          padding: 0.5rem 1rem;
          font-size: 0.75rem;
        `;
      case 'large':
        return css`
          padding: 1rem 2rem;
          font-size: 1rem;
        `;
      default:
        return css`
          padding: 0.75rem 1.5rem;
          font-size: 0.875rem;
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  svg {
    margin-right: ${({ iconRight }) => iconRight ? '0' : '0.5rem'};
    margin-left: ${({ iconRight }) => iconRight ? '0.5rem' : '0'};
  }
`;

// Create a Button component that can accept as prop and forward refs
const Button = React.forwardRef(({ as, children, ...rest }, ref) => {
  const Component = as || 'button';
  return (
    <StyledButton as={Component} ref={ref} {...rest}>
      {children}
    </StyledButton>
  );
});

export default Button;